import { AnimatePresence } from 'framer-motion';
import moment from 'moment';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useHistory } from 'react-router-dom';
import { useAppDispatch, useAppSelector } from '../../app/hooks';
import { Button, ErrorToast, SidebarModal } from '../../components';
import { AnimatedDashboard } from '../../components/animation/AnimatedDashboard';
import { InputBackOffice } from '../../components/common/InputBackoffice';
import Spinner from '../../components/common/Spinner';
import { removeSelectedTicket } from '../../slices/support-tickets-slice';
import { CaptionText, H2, H3, P1 } from '../../styles/FontStyles';
import { Flexed, PageTitle } from '../../styles/GlobalComponents';
import { colors, lightTheme } from '../../styles/GlobalStyles';
import { Label } from '../../styles/stylesStyled';
import { FileUpload, ImagesPreviewComponent2 } from './FileUpload';
import {
  addCommentSupportTicket,
  createSupportTicket,
  getCommentsSupportTicket,
  getSupportTicket
} from './SupportTickets.actions';
import { TicketComment } from './SupportTicketsStyled';
import { Tickets } from './sections/Tickets';

const SupportTickets = () => {
  const { t } = useTranslation('accounting');
  const dispatch = useAppDispatch();
  const history = useHistory();
  const { tickets, loading, selectedTicket } = useAppSelector(
    (state) => state.supportTickets
  );
  const [modal, setModal] = useState(false);
  const [modalTicket, setModalTicket] = useState(false);

  const [subject, setSubject] = useState('');
  const [message, setMessage] = useState('');
  const [images, setImages] = useState<File[]>([]);
  const [image, setImage] = useState('');

  const searchParams = new URLSearchParams(location.search);

  const ticketId = searchParams.get('ticketId');

  const setTicketId = (id: string) => {
    searchParams.set('ticketId', id);
    history.push(`${location.pathname}?${searchParams.toString()}`); // ✅ Update URL
  };

  const removeTicketId = () => {
    searchParams.delete('ticketId'); // ✅ Remove ticketId
    history.push(`${location.pathname}?${searchParams.toString()}`); // ✅ Update URL
    dispatch(removeSelectedTicket());
  };

  const handleEmptyState = () => {
    setSubject('');
    setMessage('');
    setImages([]); // Clear files after submission
  };

  useEffect(() => {
    dispatch(getSupportTicket());

    return () => {
      dispatch(removeSelectedTicket());
    };
  }, [dispatch]);

  useEffect(() => {
    if (selectedTicket.ticket) {
      setModalTicket(true);
    }
  }, [selectedTicket]);

  const handleCreateTicket = async (e: any) => {
    e.preventDefault();
    if (!subject) return ErrorToast('Please enter a subject.');
    if (!message) return ErrorToast('Please enter a message.');

    const formData = new FormData();
    formData.append('title', subject);
    formData.append('message', message);

    images.forEach((file) => {
      formData.append('files', file);
    });

    dispatch(createSupportTicket(formData));

    handleEmptyState();
    setModal(false);
  };

  const handleAddTicketComment = async (e: any) => {
    e.preventDefault();
    if (!selectedTicket.ticket) return;
    if (!message) return ErrorToast('Please enter a message.');

    const formData = new FormData();
    formData.append('message', message);

    images.forEach((file) => {
      formData.append('files', file);
    });

    dispatch(
      addCommentSupportTicket({
        id: selectedTicket.ticket.id,
        formData,
        images
      })
    );

    handleEmptyState();
  };

  useEffect(() => {
    if (tickets.length > 0 && ticketId) {
      dispatch(getCommentsSupportTicket(Number(ticketId)));
    }
  }, [ticketId, tickets]);

  const handleViewImage = (image: string) => {
    setImage(image);
  };

  return (
    <>
      {loading && <Spinner fullScreen={true} transparent />}
      <AnimatedDashboard zoom dark>
        <div style={{ marginBottom: 100 }}>
          <Flexed
            rowSM
            gap='10px'
            center
            between
            onClick={() => setModal(true)}>
            <H3 color={colors.primary900}>Support Tickets</H3>
            <Button w='300px'>Request Support</Button>
          </Flexed>

          <AnimatePresence exitBeforeEnter>
            <Tickets tickets={tickets} setTicketId={setTicketId} />
          </AnimatePresence>
        </div>
        <SidebarModal
          isOpen={modal}
          setOpen={setModal}
          title={'Request Support'}
          sidebarWidth={'700px'}>
          <Flexed column between style={{ minHeight: '100%' }}>
            <div>
              <Label>Subject</Label>
              <InputBackOffice
                value={subject}
                onChange={(e: string) => {
                  setSubject(e);
                }}
              />
              <Label>Message</Label>
              <InputBackOffice
                type='textarea'
                value={message}
                onChange={(e: string) => {
                  setMessage(e);
                }}
              />
              <FileUpload
                images={images}
                setImages={setImages}
                handleViewImage={handleViewImage}
              />
              <Button onClick={handleCreateTicket}>Send</Button>
            </div>
          </Flexed>
        </SidebarModal>

        <SidebarModal
          isOpen={selectedTicket.ticket?.id ? true : false}
          setOpen={() => removeTicketId()}
          title={selectedTicket.ticket?.title}
          sidebarWidth={'700px'}>
          <Flexed column between style={{ minHeight: '100%' }} mt='20'>
            <div>
              {selectedTicket.comments.map((comment, i) => {
                return (
                  <Flexed
                    key={i}
                    flex
                    column
                    alignEnd={!comment.isUser}
                    alignStart={comment.isUser}
                    mb='20'>
                    <TicketComment isUser={comment.isUser}>
                      <P1 style={{ wordBreak: 'break-all' }}>
                        {comment.message}{' '}
                      </P1>
                    </TicketComment>

                    <ImagesPreviewComponent2
                      images={comment.attachments}
                      handleViewImage={handleViewImage}
                    />

                    <CaptionText color={lightTheme.colors.neutral500}>
                      {moment(comment.dateCreated).format('DD.M.YYYY, hh:mm')}
                    </CaptionText>
                  </Flexed>
                );
              })}
            </div>

            {selectedTicket?.ticket?.status !== 'closed' && (
              <div>
                <Label>Message</Label>
                <InputBackOffice
                  type='textarea'
                  value={message}
                  onChange={(e: string) => {
                    setMessage(e);
                  }}
                />
                <FileUpload
                  images={images}
                  setImages={setImages}
                  handleViewImage={handleViewImage}
                />
                <Button onClick={handleAddTicketComment}>Send</Button>
              </div>
            )}

            {selectedTicket?.ticket?.status === 'closed' && (
              <SolvedClosed status={selectedTicket?.ticket?.status} />
            )}
          </Flexed>
        </SidebarModal>

        <SidebarModal
          isOpen={image ? true : false}
          setOpen={() => {
            setImage('');
          }}
          sidebarWidth={'1000px'}>
          <>
            <img src={image} style={{ width: '100%' }} />
          </>
        </SidebarModal>
      </AnimatedDashboard>
    </>
  );
};

const SolvedClosed = ({ status }: { status?: string }) => {
  return <H3 center>This ticket is {status}.</H3>;
};

export default SupportTickets;
